import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, BarChart2, Megaphone, Settings, Monitor, Globe, Clock, Radio, Mail } from 'lucide-react';
import { CookiesHero } from '../components/PageHeroes';
import { useTheme } from '../context/ThemeContext';

const Cookies = () => {
  const { isDarkMode } = useTheme();
  const [cookiePrefs, setCookiePrefs] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  const cookieTypes = [
    { icon: Shield, name: 'Essential Cookies', key: 'essential', essential: true, description: 'Required for the website to function. These include login sessions, security tokens, and load balancing. They cannot be disabled.' },
    { icon: BarChart2, name: 'Analytics Cookies', key: 'analytics', description: 'Help us understand how visitors interact with our site — which pages are visited, how long users stay, and where they come from — so we can improve performance.' },
    { icon: Megaphone, name: 'Marketing Cookies', key: 'marketing', description: 'Used to track online activity and display relevant advertising. They measure campaign effectiveness and help us reach audiences with targeted messaging.' },
    { icon: Settings, name: 'Preference Cookies', key: 'preferences', description: 'Remember your choices and settings such as language, region, and display options to deliver a more personalised experience on return visits.' }
  ];

  const managementCards = [
    { icon: Monitor, title: 'Browser Controls', description: 'Most browsers let you manage cookies via their settings menu. You can delete existing cookies and prevent new ones from being set — though this may affect site functionality.' },
    { icon: Globe, title: 'Third-Party Cookies', description: 'Some cookies are placed by third-party services we use for analytics and payment processing. Those companies operate under their own privacy and cookie policies.' },
    { icon: Clock, title: 'Cookie Retention', description: 'Session cookies are deleted when you close your browser. Persistent cookies remain on your device for a set period — typically between 1 and 2 years — unless deleted earlier.' },
    { icon: Radio, title: 'Do Not Track', description: 'There is currently no agreed industry standard for Do Not Track signals. We do not respond to DNT headers but you can always manage preferences using the toggles above.' }
  ];

  const handleToggle = (key) => {
    if (key !== 'essential') {
      setCookiePrefs(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } } };

  const p = {
    sectionBg: isDarkMode ? '#0d1117' : 'white',
    sectionAltBg: isDarkMode ? '#161b22' : '#f8fafc',
    cardBg: isDarkMode ? '#161b22' : 'white',
    cardBorder: isDarkMode ? '#30363d' : '#e2e8f0',
    heading: isDarkMode ? '#e6edf3' : '#0f1d3a',
    body: isDarkMode ? '#8b949e' : '#64748b',
    eyebrowBg: isDarkMode ? 'rgba(13, 162, 124, 0.15)' : '#e3f7f0',
    eyebrowText: isDarkMode ? '#2dd4bf' : '#0b7e62',
    iconBg: isDarkMode ? 'rgba(13, 162, 124, 0.15)' : 'rgba(13, 162, 124, 0.1)',
    activeIconBg: 'linear-gradient(135deg, #0da27c 0%, #06b6d4 100%)',
    toggleOffBg: isDarkMode ? '#30363d' : '#e2e8f0',
  };

  const sectionHeader = (eyebrow, title, subtitle) => (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 2.5rem' }}
    >
      <span style={{ display: 'inline-block', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: p.eyebrowText, background: p.eyebrowBg, padding: '0.35rem 1rem', borderRadius: '999px', marginBottom: '1rem' }}>
        {eyebrow}
      </span>
      <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 700, color: p.heading, margin: '0 0 0.75rem' }}>{title}</h2>
      {subtitle && <p style={{ fontSize: '0.98rem', color: p.body, margin: 0, lineHeight: 1.65 }}>{subtitle}</p>}
    </motion.div>
  );

  return (
    <div>
      <CookiesHero />

      {/* WHAT ARE COOKIES */}
      <section style={{ padding: '4.5rem 1.5rem', background: p.sectionBg }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', background: p.cardBg, border: `1px solid ${p.cardBorder}`, borderRadius: '1rem', padding: '2rem' }}
          >
            <div style={{ width: '48px', height: '48px', borderRadius: '0.75rem', background: p.activeIconBg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Shield size={22} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: p.heading, margin: '0 0 0.75rem' }}>What are cookies?</h2>
              <p style={{ fontSize: '0.95rem', color: p.body, lineHeight: 1.75, margin: 0 }}>
                Cookies are small text files placed on your device when you visit our website. They help us
                recognise you, remember your preferences, and understand how you use our platform. We use
                cookies to improve your experience, keep you secure, and deliver better services.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COOKIE TYPES */}
      <section style={{ padding: '4.5rem 1.5rem', background: p.sectionAltBg }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {sectionHeader('Cookie preferences', 'Types of cookies we use', "Turn optional cookies on or off. Essential cookies keep the site working and can't be disabled.")}

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {cookieTypes.map((cookie, idx) => {
              const Icon = cookie.icon;
              const isActive = cookiePrefs[cookie.key];
              return (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', background: p.cardBg, border: `1px solid ${isActive ? '#0da27c' : p.cardBorder}`, borderRadius: '0.875rem', padding: '1.25rem 1.5rem' }}
                >
                  <div style={{ width: '42px', height: '42px', borderRadius: '0.65rem', background: isActive ? p.activeIconBg : p.iconBg, color: isActive ? 'white' : '#0da27c', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={19} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: p.heading, margin: '0 0 0.3rem' }}>{cookie.name}</h3>
                    <p style={{ fontSize: '0.88rem', color: p.body, margin: 0, lineHeight: 1.6 }}>{cookie.description}</p>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {cookie.essential ? (
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0b7e62', background: p.eyebrowBg, padding: '0.4rem 0.9rem', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                        Always active
                      </span>
                    ) : (
                      <button
                        onClick={() => handleToggle(cookie.key)}
                        aria-label={`Toggle ${cookie.name}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        <span style={{ width: '40px', height: '22px', borderRadius: '999px', background: isActive ? '#0da27c' : p.toggleOffBg, position: 'relative', transition: 'background 0.2s ease' }}>
                          <span style={{ position: 'absolute', top: '2px', left: isActive ? '20px' : '2px', width: '18px', height: '18px', borderRadius: '50%', background: 'white', transition: 'left 0.2s ease' }} />
                        </span>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: p.body }}>{isActive ? 'Enabled' : 'Disabled'}</span>
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* MANAGEMENT CARDS */}
      <section style={{ padding: '4.5rem 1.5rem', background: p.sectionBg }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {sectionHeader('Cookie management', 'Managing your cookie settings', 'Everything you need to know about controlling cookies on your device.')}

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
            {managementCards.map((card, idx) => {
              const Icon = card.icon;
              return (
                <motion.div key={idx} variants={itemVariants} style={{ background: p.cardBg, border: `1px solid ${p.cardBorder}`, borderRadius: '0.875rem', padding: '1.5rem' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '0.6rem', background: p.iconBg, color: '#0da27c', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                    <Icon size={18} />
                  </div>
                  <h3 style={{ fontSize: '0.98rem', fontWeight: 700, color: p.heading, margin: '0 0 0.5rem' }}>{card.title}</h3>
                  <p style={{ fontSize: '0.85rem', color: p.body, margin: 0, lineHeight: 1.6 }}>{card.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA — fixed dark panel, correct in both themes */}
      <section style={{ padding: '4.5rem 1.5rem', background: 'linear-gradient(135deg, #0f1d3a 0%, #16284f 100%)' }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}
        >
          <div style={{ width: '52px', height: '52px', borderRadius: '0.85rem', background: 'rgba(255,255,255,0.1)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
            <Mail size={22} />
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f1f5f9', margin: '0 0 0.75rem' }}>Questions about our cookie policy?</h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', margin: '0 0 1.5rem', lineHeight: 1.65 }}>
            Our privacy team is happy to help with any questions about how we use cookies.
          </p>
          <a href="mailto:thomas@effectivestaff.co.uk" style={{ display: 'inline-block', background: '#0da27c', color: 'white', fontWeight: 600, fontSize: '0.92rem', padding: '0.8rem 1.75rem', borderRadius: '0.65rem', textDecoration: 'none' }}>
            Contact us
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default Cookies;